import React, { useCallback, useRef, useState } from "react";
import { FileText, Upload, X } from "lucide-react";
import { Button } from "@super-resume-new/ui/components/button";
import { cn } from "@super-resume-new/ui/lib/utils";

type FileUploadProps = {
  onFile: (file: File | null) => void;
  disabled?: boolean;
  /** Emerald marks the ATS booster; every other screen keeps the violet default. */
  accent?: "violet" | "emerald";
};

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export function FileUpload({ onFile, disabled, accent = "violet" }: FileUploadProps) {
  const accentText = accent === "emerald" ? "text-emerald-500" : "text-violet-500";
  const accentBorder = accent === "emerald" ? "border-emerald-500" : "border-violet-500";
  const accentHover =
    accent === "emerald" ? "hover:border-emerald-500/50" : "hover:border-violet-500/50";
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (f: File | null) => {
      if (!f) {
        setFile(null);
        onFile(null);
        return;
      }
      if (!ACCEPTED_TYPES.includes(f.type) && !/\.(pdf|doc|docx)$/i.test(f.name)) {
        alert("Please upload a PDF, DOC, or DOCX file.");
        return;
      }
      setFile(f);
      onFile(f);
    },
    [onFile],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const f = e.dataTransfer.files[0];
      if (f) handleFile(f);
    },
    [handleFile],
  );

  return (
    <div>
      {file ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2.5">
          <FileText size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="flex-1 truncate text-sm font-medium text-foreground">{file.name}</span>
          <span className="shrink-0 text-xs text-muted-foreground">
            ({(file.size / 1024).toFixed(0)} KB)
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => handleFile(null)}
            disabled={disabled}
            aria-label="Remove file"
          >
            <X aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <div
          className={cn(
            "cursor-pointer rounded-xl border border-dashed border-border bg-muted/40 px-6 py-8 text-center transition-colors",
            dragging && `${accentBorder} bg-muted/70`,
            !disabled && !dragging && `${accentHover} hover:bg-muted`,
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        >
          <Upload size={24} className="mx-auto mb-3 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            Drop your resume here or <span className={cn("font-medium", accentText)}>browse</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">PDF, DOC, or DOCX — max 5 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
